import { __rest } from "tslib";
import LoadingOutlined from '@haier/icons/LoadingOutlined';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import omit from 'rc-util/lib/omit';
import * as React from 'react';
import { ConfigConsumer, ConfigContext } from '../config-provider';
import useBuriedPoint from '../_util/hooks/useBuriedPoint';
import { cloneElement, isValidElement } from '../_util/reactNode';
import { tuple } from '../_util/type';
const LoadingSizes = tuple('small', 'default', 'large');
// Render indicator
let defaultIndicator = null;
function renderIndicator(prefixCls, props) {
    const { indicator } = props;
    const dotClassName = `${prefixCls}-dot`;
    // should not be render default indicator when indicator value is null
    if (indicator === null) {
        return null;
    }
    if (isValidElement(indicator)) {
        return cloneElement(indicator, {
            className: classNames(indicator.props.className, dotClassName),
        });
    }
    if (isValidElement(defaultIndicator)) {
        return cloneElement(defaultIndicator, {
            className: classNames(defaultIndicator.props.className, dotClassName),
        });
    }
    return (React.createElement("span", { className: classNames(dotClassName, `${prefixCls}-dot-loading`) },
        React.createElement("i", { className: `${prefixCls}-dot-item` }),
        React.createElement("i", { className: `${prefixCls}-dot-item` }),
        React.createElement("i", { className: `${prefixCls}-dot-item` }),
        React.createElement("i", { className: `${prefixCls}-dot-item` })));
}
function shouldDelay(spinning, delay) {
    return !!spinning && !!delay && !isNaN(Number(delay));
}
const Loading = (props) => {
    const { loadingPrefixCls: prefixCls, spinning: customLoadingning = true, delay, className, size = 'default', tip, wrapperClassName, style, children } = props, restProps = __rest(props, ["loadingPrefixCls", "spinning", "delay", "className", "size", "tip", "wrapperClassName", "style", "children"]);
    useBuriedPoint('Loading');
    const [spinning, setLoadingning] = React.useState(() => customLoadingning && !shouldDelay(customLoadingning, delay));
    React.useEffect(() => {
        const updateLoadingning = debounce(() => {
            setLoadingning(customLoadingning);
        }, delay);
        updateLoadingning();
        return () => {
            var _a;
            (_a = updateLoadingning === null || updateLoadingning === void 0 ? void 0 : updateLoadingning.cancel) === null || _a === void 0 ? void 0 : _a.call(updateLoadingning);
        };
    }, [delay, customLoadingning]);
    const isNestedPattern = () => typeof children !== 'undefined';
    const renderLoading = ({ direction }) => {
        const spinClassName = classNames(prefixCls, {
            [`${prefixCls}-sm`]: size === 'small',
            [`${prefixCls}-lg`]: size === 'large',
            [`${prefixCls}-spinning`]: spinning,
            [`${prefixCls}-show-text`]: !!tip,
            [`${prefixCls}-rtl`]: direction === 'rtl',
        }, className);
        // fix https://fb.me/react-unknown-prop
        const divProps = omit(restProps, ['indicator', 'prefixCls']);
        const spinElement = (React.createElement("div", Object.assign({}, divProps, { style: style, className: spinClassName, "aria-live": "polite", "aria-busy": spinning }),
            renderIndicator(prefixCls, props),
            tip ? React.createElement("div", { className: `${prefixCls}-text` }, tip) : null));
        if (isNestedPattern()) {
            const containerClassName = classNames(`${prefixCls}-container`, {
                [`${prefixCls}-blur`]: spinning,
            });
            return (React.createElement("div", Object.assign({}, divProps, { className: classNames(`${prefixCls}-nested-loading`, wrapperClassName) }),
                spinning && React.createElement("div", { key: "loading" }, spinElement),
                React.createElement("div", { className: containerClassName, key: "container" }, children)));
        }
        return spinElement;
    };
    return React.createElement(ConfigConsumer, null, renderLoading);
};
const LoadingFC = (props) => {
    const { prefixCls: customizePrefixCls } = props;
    const { getPrefixCls } = React.useContext(ConfigContext);
    const loadingPrefixCls = getPrefixCls('loading', customizePrefixCls);
    const loadingClassProps = Object.assign(Object.assign({}, props), { loadingPrefixCls });
    return React.createElement(Loading, Object.assign({}, loadingClassProps));
};
LoadingFC.setDefaultIndicator = (indicator) => {
    defaultIndicator = indicator;
};
if (process.env.NODE_ENV !== 'production') {
    LoadingFC.displayName = 'Loading';
}
LoadingFC.defaultProps = {
    indicator: React.createElement(LoadingOutlined, null),
};
export default LoadingFC;
